import React, { useEffect, useLayoutEffect, useState, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./styles/app.css";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useMsalAuthentication,
} from "@azure/msal-react";
import FileComparePage from "./component/FileComparePage/FileComparePage";
import PNPApp from "./component/PNP/PNPView";
import TestResultIngestionApp from "./component/TestResultIngestion/TestResultIngestionView";
import EventApp from "./component/Event/EventView";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { loginRequest } from "./authConfig";
import { useNavigate } from "react-router-dom";
import Navbar from "./component/NavBar/Navbar";
import Home from "./component/LandingPage/Home";
import { TimezoneProvider } from './component/common/TimezoneContext/TimezoneContext';

import SignInPage from "./component/SignIn/SignInComponet";
import DownloadPageComponent from "./component/DownloadPage/DownloadPageComponent";
import LoggerComponent from "./component/Files/LoggerComponent";
import FilePreviewComponent from "./component/FilePreview/FilePreviewComponent";
import UserList from "./component/UserList/UserList"
import DeviceRouter from "./component/Devices/DeviceRouter";
import DeviceDetailView from "./component/Devices/DeviceDetailView";
import UserDeviceView from "./component/Devices/UserDeviceView";
import AdminDeviceView from "./component/Devices/AdminDeviceView";
import UserPendingRegistrationsView from "./component/Devices/UserPendingRegistrationsView";
import TimeLineComponent from "./component/TimeLine/TimeLineComponent";
import { darkTheme, lightTheme } from "./ThemeSwitcher/ThemeProvider";
import { EventType, InteractionType } from "@azure/msal-browser";
import FileUploadComponent from "./component/common/Dialogs/FileUploadComponent";
import ProtectedRoute from "./component/common/Route/protectedRoute";
import AccessDenied from "./component/AccessDenied/accessDenied";
import DTCLogFilterApp from "./component/DTCLog/DTCLogView";
import DITApp from "./component/DIT/DITView";
import VehicleLookupView from "./component/LoggerLookup/LoggerLookupView";
import ToolsComponent from "./component/Tools/ToolsComponent";
import VisualizeDataView from "./component/VisualizeData/VisualizeDataView";
import DBcombiner from "./component/DBCombiner/DBcombiner";
import AllTaskStatusPage from "./component/DBCombiner/DBcombinerStatus";
import FilterPreferencesPage from "./component/Preferences/FilterPreferencesPage";
import { hydrateLocalStorageFromApi } from './component/Hooks/useUserPreferences';
import { NotificationProvider } from './component/common/NotificationContext';
import { routeRoleConfig } from "./component/common/Route/routeRoleConfig";
import { useDocumentTitle } from "./component/Hooks/useDocumentTitle";
import DecommissionBanner from "./component/common/DecommissionBanner";

import { CircularProgress, Box, Typography, ThemeProvider, CssBaseline } from "@mui/material";
import { useAPI } from './component/Hooks/useAPIs';


const AccessRestricted = ({ activeTheme }) => {
  const contacts = [
    { name: "Vuk", email: "vukasin.denic@gm.com" },
    { name: "Carmina Echipare", email: "carmina.echipare@gm.com" },
    { name: "Ryan Abukarn", email: "ryan.abukarn@gm.com" }
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight='95vh'
      sx={{ backgroundColor: activeTheme.palette.background.default }}
    >
      <Typography variant="h4" color="error" gutterBottom>
        Access Restricted
      </Typography>
      <Typography variant="body1" color="textPrimary" gutterBottom>
        Please contact one of the following to request access:
      </Typography>
      <Box component="ul" sx={{ mt: 2, pl: 0, listStyle: 'none' }}>
        {contacts.map((contact, index) => (
          <Box component="li" key={index} sx={{ mb: 1 }}>
            <Typography variant="body2" color="textPrimary">
              {contact.name}: <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const UserRolesContext = createContext();

const UserRolesProvider = ({ children }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const { accounts } = useMsal();

  useEffect(() => {
    if (accounts.length > 0) {
      const tokenRoles = accounts[0]?.idTokenClaims?.roles || [];
      setUserRoles(tokenRoles);
    } else {
      setUserRoles([]);
    }
    setRolesLoading(false);
  }, [accounts]);

  return (
    <UserRolesContext.Provider value={{ userRoles, rolesLoading }}>
      {children}
    </UserRolesContext.Provider>
  );
};

const PageTitleUpdater = () => {
  useDocumentTitle();
  return null;
};

export const useUserRoles = () => {
  const context = useContext(UserRolesContext);
  if (!context) {
    throw new Error("useUserRoles must be used within UserRolesProvider");
  }
  return context;
};

const MainContent = () => {
  const { login, result, error } = useMsalAuthentication(InteractionType.Redirect, loginRequest);
  const { instance, accounts } = useMsal();
  const [activeTheme, setActiveTheme] = useState(() => {
    const saved = localStorage.getItem("activeTheme");
    return saved === "dark" ? darkTheme : lightTheme;
  });
  const { rolesLoading } = useUserRoles();
  const { apiCall } = useAPI();
  
  const [isAccessAllowed, setIsAccessAllowed] = useState(null);

  useEffect(() => {
    if (instance && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
    try {
      instance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
          instance.setActiveAccount(event.payload.account);
        }
      });
    } catch (error) {
      console.error("Error during MSAL initialization or login:", error);
    }
  }, [instance]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await apiCall('post', '/api/check_access', {}, { retry: 3 });
        const data = await response.data;
        setIsAccessAllowed(data.accessAllowed);
        // Hydrate localStorage from server-side preferences on every session start
        if (data.preferences) {
          hydrateLocalStorageFromApi(data.preferences);
        }
      } catch (error) {
        console.error("Error checking access:", {
          error,
          user: accounts[0]?.username || 'unknown',
          status: error.response?.status,
        });
        setIsAccessAllowed(false);
      }
    };

    if (!rolesLoading && accounts.length > 0) {
      checkAccess();
    }
  }, [rolesLoading, accounts]);

  useLayoutEffect(() => {
    const isDark = activeTheme.palette.mode === 'dark';
    document.documentElement.classList.toggle('dark-mode', isDark);
  }, [activeTheme]);

  const handleThemeSwitch = (newTheme) => {
    setActiveTheme(newTheme);
    localStorage.setItem("activeTheme", newTheme === darkTheme ? "dark" : "light");
  };

  const RedirectToDefault = () => {
    const navigate = useNavigate();
    useEffect(() => {
      navigate("/home");
    }, [navigate]);
    return null;
  };

  if (rolesLoading || isAccessAllowed === null) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight='95vh'>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isAccessAllowed) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <AccessRestricted activeTheme={activeTheme} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <div className="App">
        <Router>
          <TimezoneProvider>
            <PageTitleUpdater />
            <AuthenticatedTemplate>
              <Navbar
                msInstance={instance}
                switchTheme={handleThemeSwitch}
                activeTheme={activeTheme}
              />
              <DecommissionBanner />
              <main className="main-content">
              <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute
                  element={<Home activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/"]}
                />
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute
                  element={<Home activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/home"]}
                />
              }
            />
            <Route
              path="/preferences"
              element={
                <ProtectedRoute
                  element={<FilterPreferencesPage activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/preferences"]}
                />
              }
            />
            <Route
              path="/files"
              element={
                <ProtectedRoute
                  element={<LoggerComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/files"]}
                />
              }
            />
            <Route
              path="/timeline"
              element={
                <ProtectedRoute
                  element={<TimeLineComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/timeline"]}
                />
              }
            />
            <Route
              path="/download"
              element={
                <ProtectedRoute
                  element={<DownloadPageComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/download"]}
                />
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute
                  element={<FileUploadComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/upload"]}
                />
              }
            />
            <Route
              path="/preview"
              element={
                <ProtectedRoute
                  element={<FilePreviewComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/preview"]}
                />
              }
            />
            <Route
              path="/file-compare"
              element={
                <ProtectedRoute
                  element={<FileComparePage />}
                  allowedRoles={routeRoleConfig["/file-compare"]}
                />
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute
                  element={<UserList activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/users"]}
                />
              }
            />

            <Route
              path="/devices"
              element={
                <ProtectedRoute
                  element={<DeviceRouter activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/devices"]}
                />
              }
            />
            <Route
              path="/devices/user"
              element={
                <ProtectedRoute
                  element={<UserDeviceView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/devices/user"]}
                />
              }
            />
            <Route
              path="/devices/user/pending"
              element={
                <ProtectedRoute
                  element={<UserPendingRegistrationsView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/devices/user"]}
                />
              }
            />
            <Route
              path="/devices/admin"
              element={
                <ProtectedRoute
                  element={<AdminDeviceView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/devices/admin"]}
                />
              }
            />
            <Route
              path="/devices/:deviceId"
              element={
                <ProtectedRoute
                  element={<DeviceDetailView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/devices"]}
                />
              }
            />


            <Route
              path="/dit"
              element={
                <ProtectedRoute
                  element={<DITApp activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["dit"]}
                />
              }
            />

            <Route
              path="/dtc_log"
              element={
                <ProtectedRoute
                  element={<DTCLogFilterApp activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />


            <Route
              path="/part_number_pull"
              element={
                <ProtectedRoute
                  element={<PNPApp activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />

            <Route
              path="/test_result_ingestion"
              element={
                <ProtectedRoute
                  element={<TestResultIngestionApp activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />

            <Route
              path="/event"
              element={
                <ProtectedRoute
                  element={<EventApp activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />

            <Route
              path="/vehicle_lookup"
              element={
                <ProtectedRoute
                  element={<VehicleLookupView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />

            <Route path="/wivi_inventory" element={<Navigate to="/devices" replace />} />

            <Route path="/wivi_inventory/:snapshotId" element={<Navigate to="/devices" replace />} />
            
            <Route
              path="/tools"
              element={
                <ProtectedRoute
                  element={<ToolsComponent activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/tools"]}
                />
              }
            />

            <Route
              path="/dbcombiner"
              element={
                <ProtectedRoute
                  element={<DBcombiner />}
                  allowedRoles={routeRoleConfig["/dbcombiner"]}
                />
              }
            />
            <Route
              path="/dbcombiner_task_status"
              element={
                <ProtectedRoute
                  element={<AllTaskStatusPage />}
                  allowedRoles={routeRoleConfig["/dbcombiner_task_status"]}
                />
              }
            />

            <Route
              path="/bus_log"
              element={
                <ProtectedRoute
                  element={<VisualizeDataView activeTheme={activeTheme} />}
                  allowedRoles={routeRoleConfig["/search"]}
                />
              }
            />
            <Route path="/access-denied" element={<AccessDenied activeTheme={activeTheme} />} />
            <Route path="*" element={<RedirectToDefault />} />
            </Routes>
            </main>
          </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
              <SignInPage />
            </UnauthenticatedTemplate>
          </TimezoneProvider>
        </Router>
      </div>
    </ThemeProvider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <UserRolesProvider>
          <MainContent />
        </UserRolesProvider>
      </NotificationProvider>
    </QueryClientProvider>
  );
};

export default App;